import React, { useContext, useState, useEffect, useRef } from "react"
import Context from "../utils/Context"
import mqtt from 'mqtt'
import { sendTaskToRobot, cancelActions } from "../utils/ROS/ROSFunctions"
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faStopCircle, faCog, faListOl, faCompass, faDolly, faChargingStation, faThList, faMinusCircle, faPlay, faArrowUp, faArrowDown, faBoxOpen, faRobot, faBatteryEmpty, faBatteryQuarter, faBatteryHalf, faBatteryThreeQuarters, faBatteryFull, faSpinner } from '@fortawesome/free-solid-svg-icons'
import ShowMessage from "../components/ShowMessage"
import api from "../api/api"
import ListOrders from "./ListOrders"
import RobotStatus from "./RobotStatus"
import BatteryStatus from "./BatteryStatus"
import RobotButtons from "./RobotButtons"

export default function Dashboard() {
  const context = useContext(Context)
  const [orders, setOrders] = useState()

  const defaultOrder = { createdBy: context.user && context.user._id, owner: context.user && context.user.owner }
  const [selectedOrder, setSelectedOrder] = useState(defaultOrder)
  const [filters, setFilters] = useState({ dtCompleted: { $exists: false }, cancelled: false })
  const [cancelled, setCancelled] = useState(null)
  const [onlineRobots, setOnlineRobots] = useState([])

  const [showWarning, setShowWarning] = useState(false)
  const [showCountdown, setShowCountdown] = useState(false)
  const [countdown, setCountdown] = useState(10)
  const [timer, setTimer] = useState(null)

  const [batteryIcon, setBatteryIcon] = useState(null)

  const listHeaders = ["mission", "robot", "start", "state", "action"]
  const refreshRate = 5000

  const isMounted = useRef(true)
  const robotStatusesRef = useRef({})
  const mqttRef = useRef()

  useEffect(() => {
    if (context.endOfMission && context.previousMission === 'Locate') {
      if (context.localizationConfidence - context.previousConfidence >= 5) context.actions.setMessage({ message: "Robô localizado!", type: "success" })
      else if (context.previousConfidence - context.localizationConfidence >= 10) context.actions.setMessage({ message: "Robô deslocalizado. Tente outra vez.", type: 'error' })
      else context.actions.setMessage({ message: "Robô já se encontrava localizado.", type: "success" })
    }
  }, [context.endOfMission])

  useEffect(() => {
    robotStatusesRef.current = context.robotStatuses

    setOnlineRobots(Object.keys(context.robotStatuses)
      .sort((a, b) => a.localeCompare(b))
      .filter(
        (robot) => context.robotStatuses[robot] === 'online'
      ))
  }, [context.robotStatuses])

  // start task
  const startTask = (mission) => {
    if (mission) {
      sendTaskToRobot({ actions: mission.actions, id: mission.name, name: mission.name })
      context.actions.setMessage({ message: "Mission started!", type: "success" })
      console.log('publishing mission: ', { actions: mission.actions, id: mission.name, name: mission.name })
    }
  }

  useEffect(() => {
    if (isMounted.current)
      api
        .fetchOrders(filters)
        .then((res) => {
          res && isMounted.current && setOrders(res.data)
        })
        .catch((e) => console.log(e))
  }, [cancelled])

  useEffect(() => {
    const refresher = setInterval(() => {

      if (isMounted.current)
        api
          .fetchOrders(filters)
          .then((res) => {
            res && isMounted.current && setOrders(res.data)
          })
          .catch((e) => console.log(e))
    }, refreshRate)

    return () => {
      if (refresher) clearInterval(refresher)
    }
  }, [])

  useEffect(() => {
    if (!context.user || !context.settings.fmsConnection) return

    // connect to mqtt
    const clientId = `mqtt_${Math.random().toString(16).slice(3)}`
    const mqttUrl = 'mqtt:' + context.settings.fmsIp + ':9001'

    console.log('connecting with id ', clientId)

    const mqttClient = mqtt.connect(mqttUrl, {
      clientId,
      clean: true,
      connectTimeout: 5000,
      reconnectPeriod: 1000,
    })

    mqttClient.on('connect', () => {
      mqttClient.subscribe('inspire/robot/+/client/status', () => {
        console.log('subscribed to robot statuses')
      })

      mqttClient.subscribe('inspire/robot/+/robotInfo', () => {
        console.log('subscribed to robot infos')
      })

      mqttClient.subscribe('inspire/robot/+/batteryStatus', () => {
        console.log('subscribed to battery statuses')
      })

      mqttClient.subscribe('inspire/robot/+/taskFeedback', () => {
        console.log('subscribed to task feedbacks')
      })

      mqttClient.subscribe('inspire/robot/+/systemDiagnostic', () => {
        console.log('subscribed to system diagnostic')
      })
    })

    mqttClient.on('offline', () => {
      console.log('MQTT went offline')
    })

    mqttClient.on('reconnect', () => {
      console.log('Reconnecting to MQTT...')
    })

    mqttClient.on('error', (error) => {
      console.error('MQTT error:', error.message)
    })

    mqttClient.on('message', (topic, payload) => {
      switch (topic.split('/')[3]) {
        case 'client':
          context.actions.setRobotStatuses({ [topic.split('/')[2]]: JSON.parse(payload).status })
          break

        case 'robotInfo':
          context.actions.setRobotInfos({ [topic.split('/')[2]]: JSON.parse(payload) })
          break

        case 'batteryStatus':
          if (robotStatusesRef.current[topic.split('/')[2]] && robotStatusesRef.current[topic.split('/')[2]] === 'online')
            context.actions.setCurrentBatteries({ [topic.split('/')[2]]: JSON.parse(payload) })
          break

        case 'taskFeedback':
          if (robotStatusesRef.current[topic.split('/')[2]] && robotStatusesRef.current[topic.split('/')[2]] === 'online')
            context.actions.setCurrentFeedback({ [topic.split('/')[2]]: JSON.parse(payload) })
          break

        case 'systemDiagnostic':
          if (robotStatusesRef.current[topic.split('/')[2]] && robotStatusesRef.current[topic.split('/')[2]] === 'online')
            context.actions.setCurrentErrors({ [topic.split('/')[2]]: JSON.parse(payload).flags })
          break

        default:
          break
      }
    })

    mqttRef.current = mqttClient
    context.actions.setMqttClient(mqttClient)

    return () => {
      if (mqttClient) {
        mqttClient.end()
        console.log('mqtt disconnected')
      }
    }
  }, [context.user, context.settings.fmsConnection])

  const cancelledSet = cancel => setCancelled(cancel)

  const selectOrder = (orderId) => {
    if (!orderId) {
      setSelectedOrder(defaultOrder)
      return false
    }
    setSelectedOrder(orders.find((order) => order._id === orderId))
  }

  const startDistribution = async () => {
    try {
      await api.startDistributions({
        robotId: context.robotInfo?._id ? context.robotInfo._id : "668569a25067644208b2c3fe",
        robotName: context.robotInfo?.name ? context.robotInfo.name : "1000PT",
        owner: context.robotInfo?.owner ? context.robotInfo.owner : "SONAE",
        createdBy: context.user ? context.user._id : '',
        operationId: context.operation ? context.operation._id : '66857bb15067644208b3827c'
      })

      context.actions.setMessage({ message: "Distribuição iniciada!", type: "success" })
    } catch (error) {
      context.actions.setMessage({ message: "Erro a começar", type: "error" })
    }
  }

  const startReturn = async () => {
    try {
      await api.startReturns({
        robotId: context.robotInfo?._id ? context.robotInfo._id : "668569a25067644208b2c3fe",
        robotName: context.robotInfo?.name ? context.robotInfo.name : "1500PT",
        owner: context.robotInfo?.owner ? context.robotInfo.owner : "SONAE",
        createdBy: context.user ? context.user._id : '',
        operationId: context.operation ? context.operation._id : '66857bb15067644208b3827c'
      })

      context.actions.setMessage({ message: "Retorno iniciado!", type: "success" })
    } catch (error) {
      context.actions.setMessage({ message: "Erro a começar", type: "error" })
    }
  }

  // execute a mission
  const execMission = (missionName) => {
    const mission = context.availableMissions.find(m => m.name === missionName)
    const hasUV = mission?.actions?.find(action => action.name === "desinfect-uv" || action.name === "desinfect-all")

    if (mission) {
      // console.log("found mission", mission)

      context.actions.setCurrentMission({ ...mission, hasUV: hasUV ? true : false })
    }

    if (hasUV) {
      setShowWarning(true)
    } else {
      startTask(mission)
    }
  }

  const startCharger = () => {

    var string = {
      "robot": "1500PT",
      "mapIndex": 0,
      "name": "Charger",
      "actions": [
        {
          "id": "go-to",
          "name": "go-to",
          "needsValue": true,
          "value": "A_Charger"
        },
        {
          "id": "dock",
          "name": "dock",
          "needsValue": true,
          "value": "charger"

        },
        {
          "id": "orientate-to",
          "name": "orientate-to",
          "needsValue": true,
          "value": "Charger"
        }
      ]
    }

    startTask(string)

  }

  const frente = async () => {
    var string = {
      "robot": "1500PT",
      "mapIndex": 0,
      "name": "fork",
      "actions": [
        {
          "id": "fork",
          "name": "fork",
          "needsValue": true,
          "value": "0.1"
        }
      ]
    }

    startTask(string)
  }

  const tras = async () => {
    var string = {
      "robot": "1500PT",
      "mapIndex": 0,
      "name": "fork",
      "actions": [
        {
          "id": "fork",
          "name": "fork",
          "needsValue": true,
          "value": "0.0"
        }
      ]
    }

    startTask(string)

  }

  const stopMission = async () => {
    if (context.isRobotConnected) {
      if (context.selectedRobot.ROS === 'ROS2') {
        cancelActions({ stopall: true }, context.selectedRobot.ROS)
      } else {
        cancelActions({ stopAll: true }, context.selectedRobot.ROS)
      }
    } else {
      onlineRobots.forEach(robot => {
        mqttRef.current.publish('inspire/fms/' + robot + '/cancel', JSON.stringify({ order: "stopAll" }), { qos: 0, retain: false }, (error) => {
          if (error) {
            console.error(error)
          }
        })
      })
    }

    context.actions.setCurrentMission(null)
    try {
      await api.cancelAllOrders(context.selectedRobot ? { robotName: context.selectedRobot.name } : null)
      await api.processAllTriggers(context.selectedRobot ? { robotName: context.selectedRobot.name } : null)

      //api.deleteUsed(1)
    } catch (error) {
      context.actions.setMessage({ message: "Erro a começar no servidor", type: "error" })
    }

    context.actions.setMessage({ message: "Missão parada!", type: "success" })

  }

  const execLocation = () => {

    sendTaskToRobot({ actions: [{ value: "Base", name: "publish-pose" }], id: "Base", name: "PRIORITY" })
    context.actions.setMessage({ message: "Ok!", type: "success" })
    context.actions.setPreviousConfidence(context.localizationConfidence)
  }

  // end of countdown
  useEffect(() => {
    if (countdown === 0) {
      clearInterval(timer)
      setTimer(null)
      setShowCountdown(false)
      setCountdown(10)
      startTask(context.currentMission)
      setShowWarning(false)
    }
  }, [countdown, context.currentMission, timer])

  return (
    <div>
      {context.settings.fmsConnection &&
        <>
          {context.isRobotConnected &&
            <>
              <button
                onClick={() => context.actions.setModalState({ ...context.modalState, missions: true })}
                className="button is-success mb-5 mr-3"
                styles={{ marginRight: "1rem" }}
              >
                <FontAwesomeIcon icon={faPlus} className="is-small mr-7" />
                Manage Missions
              </button>

              <button className="button is-warning mr-2 mb-2" onClick={() => startCharger()}>
                <FontAwesomeIcon icon={faChargingStation} className="is-small mr-1" />
                Charge
              </button>

              <button className="button is-info mr-2 mb-2" onClick={() => context.actions.setModalState({ locate: true })}>
                <FontAwesomeIcon icon={faCompass} className="is-small mr-1" />
                Locate
              </button>

              <button className="button is-outlined is-link mr-2 mb-2" onClick={() => frente()}>
                <FontAwesomeIcon icon={faDolly} className="is-small mr-1" />
                <FontAwesomeIcon icon={faArrowUp} className="is-small mr-1" />
                Raise Forks
              </button>

              <button className="button is-outlined is-link mr-2 mb-2" onClick={() => tras()}>
                <FontAwesomeIcon icon={faDolly} className="is-small mr-1" />
                <FontAwesomeIcon icon={faArrowDown} className="is-small mr-1" />
                Lower Forks
              </button>
            </>
          }

          <button
            onClick={stopMission}
            className="button is-danger mb-5 mr-3"
          >
            <FontAwesomeIcon icon={faStopCircle} className="is-small mr-1" />
            Cancel All
          </button>

          <br />

          {context.availableMissions.length > 0 && context.isRobotConnected &&
            context.availableMissions.map((mission, i) => {
              if (mission.robot === context.selectedRobot?.name) {
                return <button key={i} className="button is-outlined is-link mr-2 mb-5" onClick={() => execMission(mission.name)}>
                  {mission.name + " "}
                </button>
              }
            })}

          <div className="box columns is-multiline" style={{ borderRadius: "15px", textAlign: "center", width: '100%' }}>
            <div className="column is-12-mobile is-12-tablet is-6-desktop" style={{ borderRight: window.innerWidth >= 1024 ? "groove 1px black" : '', borderBottom: window.innerWidth < 1024 ? 'groove 1px black' : '' }}>
              {onlineRobots.length === 0 ? (
                <div style={{ padding: '1rem', textAlign: 'center' }}>
                  No robots online!
                </div>
              ) : (
                onlineRobots.map((robot, i) => {
                  return <div className="columns is-vcentered" id={robot} style={{ marginTop: i === 0 ? '' : '8px' }} key={i}>
                    {context.robotInfos[robot] &&
                      <div className='column is-one-third' style={{ display: 'flex', justifyContent: 'left' }}>
                        <RobotButtons robot={robot} />
                      </div>}

                    <div className='column is-one-third'>
                      <RobotStatus robot={robot} />
                    </div>

                    <div className='column is-one-third'>
                      <BatteryStatus robot={robot} />
                    </div>
                  </div>
                }))}
            </div>

            {orders &&
              <div className="column is-12-mobile is-12-tablet is-6-desktop">
                <ListOrders data={orders} handler={selectOrder} headers={listHeaders} cancelled={cancelledSet} cancelledValue={cancelled} />
              </div>
            }
          </div>
          <ShowMessage />
        </>
      }
    </div>
  )
}